/*
 * skin-experience-logic.js
 * Pure, dependency-free helpers shared by the skin quiz, quiz results, and routine builder.
 * Loaded as a classic <script> in the theme (sets window.SkinExp) AND require()-able in Jest.
 *
 * Fixes (see docs/superpowers/specs/2026-06-25-quiz-results-routine-redesign-design.md):
 *  - Q2b: SPF "Untinted" was mapped to "Tinted" by a `.includes("tinted")` substring trap.
 *  - R5b: quiz concerns ("Anti-Aging"/"Dryness") were not in the engine's VALID_CONCERNS,
 *         so results silently failed to personalize. Map to the canonical vocabulary.
 *  - R12/B2: money is integer cents end-to-end, converted to dollars exactly once.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
  if (root) { root.SkinExp = api; }
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : this), function () {
  'use strict';

  // Q2b — exact/anchored match. The original bug used color.includes("tinted"),
  // and "untinted".includes("tinted") === true, so Untinted was unreachable.
  function normalizeSunscreenColor(value) {
    var v = String(value == null ? '' : value).trim().toLowerCase();
    if (v === 'untinted' || v.indexOf('untinted') !== -1) return 'Untinted';
    if (v === 'tinted' || v.indexOf('tinted') !== -1) return 'Tinted';
    return 'Untinted';
  }

  // R5b — map quiz concern strings (raw Q4 answers AND legacy mapSkinConcerns outputs)
  // to the engine's VALID_CONCERNS vocabulary. Unknown -> null so callers can drop it
  // (the engine then falls back to a skin-type / default routine instead of mis-matching).
  var CONCERN_CANONICAL = {
    // raw Q4 answer values (sections/skin-quiz.liquid:144-159)
    'acne and breakouts': 'Acne-Prone',
    'tight and flaky dry skin': 'Dehydration',
    'fine lines and wrinkles': 'Fine Lines/Wrinkles',
    'hyperpigmentation/discoloration': 'Hyperpigmentation',
    'redness and rosacea': 'Redness/Rosacea',
    // legacy mapSkinConcerns outputs
    'acne-prone': 'Acne-Prone',
    'dryness': 'Dehydration',
    'dehydration': 'Dehydration',
    'anti-aging': 'Fine Lines/Wrinkles',
    'fine lines/wrinkles': 'Fine Lines/Wrinkles',
    'hyperpigmentation': 'Hyperpigmentation',
    'redness/rosacea': 'Redness/Rosacea',
    'redness-prone': 'Redness/Rosacea'
  };

  function mapConcernToCanonical(value) {
    var v = String(value == null ? '' : value).trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(CONCERN_CANONICAL, v) ? CONCERN_CANONICAL[v] : null;
  }

  // R12/B2 — input is INTEGER CENTS; convert to dollars by dividing by 100 exactly once.
  function formatMoneyFromCents(cents, symbol) {
    var c = Math.round(Number(cents) || 0);
    var sym = (symbol == null || symbol === '') ? '$' : symbol;
    return sym + (c / 100).toFixed(2);
  }

  function sumCents(list) {
    return (list || []).reduce(function (acc, n) { return acc + Math.round(Number(n) || 0); }, 0);
  }

  // Q3 — step counter text "n / total", clamped to [1, total].
  function stepCounterText(n, total) {
    var t = Math.max(1, Math.round(Number(total) || 1));
    var c = Math.round(Number(n));
    if (!isFinite(c)) c = 1;
    c = Math.min(Math.max(c, 1), t);
    return c + ' / ' + t;
  }

  // ------------------------------------------------------------------
  // Bundle maths: "add N products from your routine and save X%".
  //
  // RETIRED (Oct 2026). The routine builder and quiz results no longer read a
  // bundle config or apply a discount code: the Bundle20 mechanic was dropped in
  // favour of a merchant-edited offer callout. These pure helpers stay only for
  // the unloaded legacy section-skin-quiz-new.js; with no config they are inert
  // (no discount, no message). There is deliberately no code here that writes a
  // discount to the cart.
  // ------------------------------------------------------------------

  // `count` is ELIGIBLE ITEMS (see bundleEligibleCount) — the same thing
  // Shopify's minimum-quantity rule counts, so the promise on the page and the
  // discount that actually fires agree.
  function bundleProgress(distinctCount, config) {
    var cfg = config || {};
    var n = Math.max(0, Math.round(Number(distinctCount) || 0));
    if (!cfg.enabled) return { enabled: false, qualifies: false, remaining: 0, count: n };
    var remaining = Math.max(0, cfg.threshold - n);
    return {
      enabled: true,
      count: n,
      qualifies: remaining === 0,
      remaining: remaining,
      threshold: cfg.threshold,
      percent: cfg.percent
    };
  }

  function bundleMessage(progress) {
    if (!progress || !progress.enabled) return '';
    var pct = progress.percent ? String(progress.percent).replace(/\.0+$/, '') : '';
    if (progress.qualifies) {
      return pct ? ('✓ ' + pct + '% off applied at checkout') : '✓ Bundle discount applied at checkout';
    }
    // Nothing picked yet: state the offer rather than counting down from it, so
    // the promotion is legible before the shopper has done anything.
    if (progress.count === 0) {
      return pct
        ? ('Add ' + progress.threshold + ' or more products and save ' + pct + '%')
        : ('Add ' + progress.threshold + ' or more products to unlock your bundle discount');
    }
    var n = progress.remaining;
    var noun = n === 1 ? 'product' : 'products';
    return pct
      ? ('Add ' + n + ' more ' + noun + ' to save ' + pct + '%')
      : ('Add ' + n + ' more ' + noun + ' to unlock your bundle discount');
  }

  // What the shopper pays, before and after the bundle discount.
  //
  // The discount covers ONLY eligible lines, so a routine of $502.50 that
  // includes a $258 excluded serum is not $402 — it is $453.60. Showing a flat
  // percentage off the whole basket would overstate the saving on exactly the
  // carts most likely to contain an excluded product.
  //
  // Shopify allocates per line (allocation_method: "each") and FLOORS each
  // allocation — it does not round, and it does not take a percentage of the
  // basket. Established by putting three x.99 products through the live cart:
  // 1499/3799/3099 came back discounted 299/759/619, total 1677. Rounding
  // per line would have predicted 1680 and a percentage of the whole cart 1679,
  // so both would have quoted a saving the cart then refused to honour. About
  // 11% of this catalogue is priced where that difference is real.
  //
  // `items` is a list of { priceCents, quantity, eligible }.
  function bundleTotals(items, config) {
    var cfg = config || {};
    var list = items || [];
    var originalCents = 0;
    var discountCents = 0;
    var eligibleCount = 0;
    var pct = (cfg.enabled && isFinite(cfg.percent)) ? Number(cfg.percent) : 0;

    for (var i = 0; i < list.length; i++) {
      var it = list[i] || {};
      var qty = parseInt(it.quantity, 10);
      qty = (isFinite(qty) && qty > 0) ? qty : 1;
      var line = Math.round(Number(it.priceCents) || 0) * qty;
      originalCents += line;
      if (it.eligible !== false) {
        eligibleCount += qty;
        // The epsilon guards the exact cases (a whole-dollar line whose 20%
        // lands on a clean cent) against a binary-floating-point result that
        // sits a hair under the integer; it cannot promote a genuinely
        // fractional allocation like 299.8.
        discountCents += Math.floor((line * pct) / 100 + 1e-9);
      }
    }

    var progress = bundleProgress(eligibleCount, cfg);
    if (!progress.qualifies) discountCents = 0;

    return {
      originalCents: originalCents,
      discountCents: discountCents,
      finalCents: originalCents - discountCents,
      eligibleCount: eligibleCount,
      qualifies: progress.qualifies,
      progress: progress
    };
  }

  // Write a total into an element, slashing the old price once the bundle
  // qualifies. Uses innerHTML only for the two spans it builds itself — the
  // money strings come from formatMoneyFromCents, never from user input.
  function renderBundleTotal(el, totals, options) {
    if (!el) return;
    var opts = options || {};
    var sym = opts.symbol || '$';
    var prefix = opts.prefix || '';
    var t = totals || { originalCents: 0, finalCents: 0, discountCents: 0 };

    if (!t.discountCents) {
      el.textContent = prefix + formatMoneyFromCents(t.originalCents, sym);
      el.classList.remove('has-bundle-discount');
      return;
    }
    el.classList.add('has-bundle-discount');
    el.innerHTML =
      (prefix ? '<span class="ebc-total-prefix">' + prefix + '</span>' : '') +
      '<s class="ebc-total-was">' + formatMoneyFromCents(t.originalCents, sym) + '</s>' +
      '<span class="ebc-total-now">' + formatMoneyFromCents(t.finalCents, sym) + '</span>';
  }

  // Count only the units Shopify will actually discount.
  //
  // The code is scoped to a collection, and Shopify's minimum-quantity rule
  // counts ONLY the items that scope entitles. Verified live: three products
  // where one was an excluded vendor came back `applicable: false`. Counting
  // every selection made the page promise a discount the cart then refused, so
  // anything flagged ineligible (snippets/bundle-eligibility.liquid) is skipped.
  //
  // `items` is a list of { quantity, eligible } — quantity defaults to 1, and
  // eligibility defaults to true so a missing flag can never hide a product
  // that does qualify.
  function bundleEligibleCount(items) {
    return (items || []).reduce(function (n, item) {
      if (!item || item.eligible === false) return n;
      var q = parseInt(item.quantity, 10);
      return n + (isFinite(q) && q > 0 ? q : 1);
    }, 0);
  }

  // ---- impure: touches the DOM / the Shopify cart ---------------------------
  // Everything above is pure and unit-testable. What follows is here rather than
  // duplicated in the results page and the routine builder, because both have to
  // get the same subtle rules right.

  // Scroll a step into view inside whatever element actually scrolls.
  //
  // Both routine pages render inside a FIXED full-screen overlay, so the window
  // never scrolls; the element that does (.ebc-routine-content) sits behind a
  // wrapper with overflow:hidden. scrollIntoView() walks up looking for a
  // scrollport, gives up at that hidden wrapper and does nothing — measured on
  // the live results page, clicking STEP 06 left scrollTop at 0 with the step
  // 3,904px further down. That is why the step list looked inert.
  //
  // So find the real scrollport and do the arithmetic. Returns true if an inner
  // scroller was used, false if it fell back to the document.
  function scrollStepIntoView(target, options) {
    if (!target || typeof target.getBoundingClientRect !== 'function') return false;
    var opts = options || {};
    var offset = Number(opts.offset) || 0;
    var behavior = opts.behavior || 'smooth';
    var canCompute = typeof getComputedStyle === 'function';

    var node = target.parentElement;
    while (node && node !== document.body && node !== document.documentElement) {
      var oy = canCompute ? getComputedStyle(node).overflowY : '';
      if ((oy === 'auto' || oy === 'scroll') && node.scrollHeight > node.clientHeight + 1) {
        var delta = target.getBoundingClientRect().top - node.getBoundingClientRect().top;
        var top = node.scrollTop + delta - offset;
        if (typeof node.scrollTo === 'function') {
          node.scrollTo({ top: top, behavior: behavior });
        } else {
          node.scrollTop = top;
        }
        return true;
      }
      node = node.parentElement;
    }

    // No inner scrollport — an ordinary page, where scrollIntoView is correct.
    target.scrollIntoView({ behavior: behavior, block: 'start' });
    return false;
  }

  return {
    normalizeSunscreenColor: normalizeSunscreenColor,
    mapConcernToCanonical: mapConcernToCanonical,
    CONCERN_CANONICAL: CONCERN_CANONICAL,
    formatMoneyFromCents: formatMoneyFromCents,
    sumCents: sumCents,
    stepCounterText: stepCounterText,
    bundleProgress: bundleProgress,
    bundleMessage: bundleMessage,
    bundleEligibleCount: bundleEligibleCount,
    bundleTotals: bundleTotals,
    renderBundleTotal: renderBundleTotal,
    scrollStepIntoView: scrollStepIntoView
  };
});
